import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlanStore } from '@/stores';
import type { PlanLimitError } from '@/types';
import { UpgradeModal } from './UpgradeModal';

/**
 * Opens the UpgradeModal for two distinct upgrade moments. Mount once, high in
 * the authenticated tree (AppLayout). Mirrors the `auth:unauthorized` →
 * useAuthRedirect convention.
 *
 *   • `billing:limit-reached` — httpClient 402 (PLAN_LIMIT_REACHED /
 *     FEATURE_NOT_IN_PLAN, LIL-341): user HAS a plan but hit a cap. CTA goes to
 *     the billing page to pick a higher tier.
 *   • `billing:feature-locked` — dispatched by the Sidebar (and other in-app
 *     entry points) when a NO-plan user clicks a premium feature. CTA goes to
 *     the full-screen Paywall (`/app/upgrade`) to subscribe.
 *
 * Note: the background `billing:subscription-required` event (a stray
 * NO_ACTIVE_PLAN 402 from a page's data call) is intentionally NOT handled here
 * — it must not pop a modal during normal browsing. Gating is proactive
 * (Sidebar locks + FeatureGate), so that event is left to the page's own state.
 */
export function UpgradeListener() {
  const navigate = useNavigate();
  const refreshUsage = usePlanStore(s => s.refreshUsage);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<PlanLimitError | null>(null);
  // Where the CTA should send the user — billing page (tier upgrade) vs the
  // full paywall (no plan). Defaults to billing.
  const [ctaTarget, setCtaTarget] = useState('/app/billing');

  useEffect(() => {
    const onLimitReached = (e: Event) => {
      const detail = (e as CustomEvent).detail as PlanLimitError | undefined;
      setError(detail ?? null);
      setCtaTarget('/app/billing');
      setOpen(true);
      // The limit was just hit — pull fresh usage so the sidebar meter/badge
      // reflect reality immediately rather than on the next page load.
      void refreshUsage();
    };

    const onFeatureLocked = (e: Event) => {
      const detail = (e as CustomEvent).detail as { feature?: string } | undefined;
      const feature = detail?.feature ?? 'This feature';
      setError({
        code: 'NO_ACTIVE_PLAN',
        message: `${feature} is a premium feature. Subscribe to unlock it and the rest of your LiLi workspace.`,
      } as PlanLimitError);
      setCtaTarget('/app/upgrade');
      setOpen(true);
    };

    window.addEventListener('billing:limit-reached', onLimitReached);
    window.addEventListener('billing:feature-locked', onFeatureLocked);
    return () => {
      window.removeEventListener('billing:limit-reached', onLimitReached);
      window.removeEventListener('billing:feature-locked', onFeatureLocked);
    };
  }, [refreshUsage]);

  const handleUpgrade = () => {
    setOpen(false);
    navigate(ctaTarget);
  };

  return (
    <UpgradeModal open={open} onOpenChange={setOpen} error={error} onUpgrade={handleUpgrade} />
  );
}
